// Version checking utility for cache busting
// Checks server version periodically and reloads if new version detected

interface VersionInfo {
  version: string;
  buildTime: string;
}

let currentVersion: string | null = null;
let checkInterval: NodeJS.Timeout | null = null;
// Callback to clear caches when version changes (e.g., tile cache)
let cacheClearCallback: (() => void) | null = null;

/**
 * Get the current app version from version.json
 * Uses cache-busting query parameter to ensure fresh fetch
 */
export async function getCurrentVersion(): Promise<string | null> {
  try {
    // Add timestamp query param to bypass browser cache
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store', // Explicitly tell browser not to cache
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      }
    });
    
    if (!response.ok) {
      console.warn('Failed to fetch version.json:', response.status);
      return null;
    }
    
    const data: VersionInfo = await response.json();
    const version = data.version;
    // Cache the version for quick access
    currentVersion = version;
    return version;
  } catch (error) {
    console.warn('Failed to fetch version:', error);
    return null;
  }
}

/**
 * Register a callback to clear caches when version changes
 * Useful for clearing tile cache, etc.
 */
export function registerCacheClearCallback(callback: () => void): () => void {
  cacheClearCallback = callback;
  // Return unregister function
  return () => {
    if (cacheClearCallback === callback) {
      cacheClearCallback = null;
    }
  };
}

/**
 * Add cache-busting query parameter to a URL
 * Uses current version if available, otherwise uses timestamp
 */
export function addCacheBusting(url: string): string {
  const version = currentVersion || Date.now().toString();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_v=${version}`;
}

/**
 * Initialize version checking
 * Checks every 2 minutes and reloads if version changed
 * 
 * @returns Cleanup function to stop version checking
 */
export function startVersionChecking(): () => void {
  // Get initial version on app load
  getCurrentVersion().then(version => {
    currentVersion = version;
    if (version) {
      console.log('App version:', version);
    }
  });

  // Check for updates every 2 minutes
  // This interval is reasonable for mobile users - not too frequent, but catches updates quickly
  checkInterval = setInterval(async () => {
    const serverVersion = await getCurrentVersion();
    
    if (serverVersion && currentVersion && serverVersion !== currentVersion) {
      console.log('🔄 New version detected! Reloading app...', {
        current: currentVersion,
        new: serverVersion
      });
      
      // Clear any registered caches (e.g., tile cache) before reloading
      if (cacheClearCallback) {
        try {
          cacheClearCallback();
        } catch (error) {
          console.warn('Error clearing cache:', error);
        }
      }
      
      // Force reload with cache bypass for mobile browsers
      // Add cache-busting query parameter to ensure fresh fetch
      const url = new URL(window.location.href);
      // Remove existing cache-bust params if any
      url.searchParams.delete('_v');
      url.searchParams.delete('_reload');
      // Add new cache-busting params using the new version
      url.searchParams.set('_v', serverVersion);
      url.searchParams.set('_reload', '1');
      
      // Use replace to avoid adding to history, and force cache bypass
      // This ensures mobile browsers fetch fresh content
      window.location.replace(url.toString());
    } else if (serverVersion && !currentVersion) {
      // First successful fetch - store it
      currentVersion = serverVersion;
    }
  }, 2 * 60 * 1000); // Check every 2 minutes

  // Return cleanup function
  return () => {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  };
}

/**
 * Get the cached current version synchronously
 * Returns null if version hasn't been fetched yet
 */
export function getCachedVersion(): string | null {
  return currentVersion;
}

/**
 * Get the current version, fetching if not cached
 * This is useful for components that need the version immediately
 */
export async function getVersion(): Promise<string | null> {
  if (currentVersion) {
    return currentVersion;
  }
  const version = await getCurrentVersion();
  if (version) {
    currentVersion = version;
  }
  return version;
}
